import { useEffect, useState } from "react";
import {
  AppBorderBox,
  AppBox,
  AppButton,
  AppModal,
  AppPaper,
  AppSkeletonGlobalCardList,
  AppText,
  AppTitle,
} from "../../../../../commons/components";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { FootballTeamCoach } from "../../../../../commons/models/football/football-team-coach";
import {
  fetchManageTeamCoachList,
  IManageTeamCoachsOption,
} from "../../../../../redux/slices/football/admin/team/coach/manage-team-coaches-slice";
import UserSearch from "../../../users/components/UserSearch";
import TeamCoachRemove from "../teamCoach/TeamCoachRemove";
import { IFootballTeamCoach } from "../../../../../commons/models/football/interface/i-football-team-coach";
import IconPersonDelete from "../../../../../commons/components/icons/person-delete";
import TeamCoachRemovalInprogress from "../teamCoach/TeamCoachRemoveInprogress";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import { teamCoachRemove } from "../../../../../redux/slices/football/admin/team/coach/manage-team-coach-remove-slice";
import { Can } from "../../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import IconPersonAdd from "../../../../../commons/components/icons/person-add";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import { User } from "../../../../../commons/models/user";
import TeamCoachCard from "../teamCoach/TeamCoachCard";
import { teamCoachAdd } from "../../../../../redux/slices/football/admin/team/coach/manage-team-coach-add-slice";
import TeamCoachAddConfirm from "../teamCoach/TeamCoachAddConfirmation";

export default function TeamCoach() {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { teamInfo } = useAppSelector((state) => state.footballManageTeam);
  const [isCoachAdd, setCoachAdd] = useState(false);
  const [selectedTeamCoach, setSelectedTeamCoach] = useState<FootballTeamCoach | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { manageTeamCoachsList, manageTeamCoachsListStatus } = useAppSelector(
    (state) => state.footballManageTeamCoaches
  );
  const { manageTeamCoachRemoveStatus } = useAppSelector((state) => state.footballManageTeamCoachRemove);
  const { manageTeamCoachAddStatus } = useAppSelector((state) => state.footballManageTeamCoachAdd);
  const [isRemoveTeamCoach, setRemoveTeamCoach] = useState(false);

  useEffect(() => {
    if (teamInfo.id) {
      const coachListOption: IManageTeamCoachsOption = {
        teamId: teamInfo.id,
      };
      dispatch(fetchManageTeamCoachList(coachListOption));
    }
  }, [teamInfo]);

  const handleCoachAdd = () => {
    if (selectedUser) {
      dispatch(
        teamCoachAdd({
          teamId: teamInfo.id,
          userId: selectedUser.id,
        })
      )
        .unwrap()
        .then(() => {
          setSelectedTeamCoach(null);
          addSnackbar({
            key: "coach-add",
            text: "Coach Added Successfully",
            variant: "success",
          });
          dispatch(
            fetchManageTeamCoachList({
              teamId: teamInfo.id,
            })
          );
          setSelectedTeamCoach(null);
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
    setSelectedUser(null);
  };

  const handleCoachRemoveConfirm = () => {
    if (selectedTeamCoach) {
      dispatch(
        teamCoachRemove({
          teamId: teamInfo.id,
          coachId: selectedTeamCoach.id,
        })
      )
        .unwrap()
        .then(() => {
          setSelectedTeamCoach(null);
          addSnackbar({
            key: "coach-remove",
            text: "Coach Removed Successfully",
            variant: "success",
          });
          dispatch(
            fetchManageTeamCoachList({
              teamId: teamInfo.id,
            })
          );
          setSelectedTeamCoach(null);
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
  };

  return (
    <>
      <AppBox gap="sm" flexDirection="column">
        <AppBox alignItems="center" justifyContent="space-between">
          <AppTitle as="h5">Coach</AppTitle>
        </AppBox>
        {manageTeamCoachsList.length === 0 ? (
          <AppPaper padding="sm" shadow="xs">
            <AppBorderBox borderVariant="dashed">
              <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center" p="sm">
                <AppBox alignItems="center" justifyContent="center" gap="xs">
                  <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                    <AppBox>
                      <IconPersonAdd width={40} height={40} color="rgba(var(--border-200))" />
                    </AppBox>
                  </Can>
                  <AppText fontWeight="semibold">There is not any coach in the team.</AppText>
                </AppBox>
                <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                  <AppButton size="sm" label="Add Coach" onClick={() => setCoachAdd(true)} />
                </Can>
              </AppBox>
            </AppBorderBox>
          </AppPaper>
        ) : (
          <>
            {manageTeamCoachsListStatus === StatusEnum.Loading ? (
              <>
                <AppSkeletonGlobalCardList numberOfItems={2} withAvatar noTitle />
              </>
            ) : (
              <AppPaper padding="none" shadow="xs">
                <AppBox flexDirection="column">
                  {manageTeamCoachsList.all().map((coach, index) => (
                    <AppBorderBox
                      key={index}
                      className="w-100"
                      border={
                        manageTeamCoachsList.length === 1
                          ? []
                          : index === 0
                          ? ["Bottom"]
                          : index === manageTeamCoachsList.length - 1
                          ? ["Top"]
                          : ["Top", "Bottom"]
                      }
                    >
                      <TeamCoachCard
                        coachInfo={coach}
                        onCoachRemove={() => {
                          setSelectedTeamCoach(coach);
                          setRemoveTeamCoach(true);
                        }}
                      />
                    </AppBorderBox>
                  ))}
                </AppBox>
              </AppPaper>
            )}
          </>
        )}
      </AppBox>
      <AppModal
        opened={isCoachAdd}
        onClose={() => {
          setCoachAdd(false);
        }}
        titleIcon={<IconPersonAdd />}
        title={"Choose A Coach"}
        withoutButtons
      >
        <UserSearch
          userSelectedUsers={manageTeamCoachsList.length > 0 ? (manageTeamCoachsList.all() as User[]) : []}
          onSelectedUsers={(users) => {
            setCoachAdd(false);
            setSelectedUser(users[0]);
          }}
          teamAvailableUsers
          team={teamInfo}
        />
      </AppModal>
      {selectedUser && (
        <AppModal
          opened={selectedUser !== null}
          onClose={() => {
            setSelectedUser(null);
          }}
          titleIcon={<IconPersonAdd />}
          title={"Add Coach To Team"}
          onSave={() => {
            handleCoachAdd();
          }}
          withLoadingButton={manageTeamCoachAddStatus === StatusEnum.Loading}
        >
          <TeamCoachAddConfirm user={selectedUser} />
        </AppModal>
      )}
      <AppModal
        opened={isRemoveTeamCoach}
        onClose={() => {
          setRemoveTeamCoach(false);
          setSelectedTeamCoach(null);
        }}
        title="Want To Remove This Coach?"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        confirmButtonLabel="Remove"
        onSave={() => {
          setRemoveTeamCoach(false);
          handleCoachRemoveConfirm();
        }}
      >
        <TeamCoachRemove teamCoach={selectedTeamCoach as IFootballTeamCoach} />
      </AppModal>
      <AppModal
        opened={manageTeamCoachRemoveStatus === StatusEnum.Loading}
        title="Removing Coach"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        withoutCancelButton
        withoutButtons
        withoutCloseButton
      >
        <TeamCoachRemovalInprogress teamCoach={selectedTeamCoach as IFootballTeamCoach} />
      </AppModal>
    </>
  );
}
